// Two-tier filter sidebar — Stream 3 PR-4e (2026-05-15).
//
// Replaces the old horizontal MainPanelHeader filter row + the
// orphaned .preferencesGroup pill strip from PR-4d. Mirrors the
// design spec in docs/design/stream-3-addons-search-detail.md
// §"Two-tier filter sidebar":
//
//   ┌──────────────────────────────┐
//   │ Must have      [Required]    │   <- hard filters (Sharetribe)
//   │   Price / Guest count /      │
//   │   Fulfillment …              │
//   ├──────────────────────────────┤
//   │ Nice to have   [Preference]  │   <- client-side re-rank
//   │   Decor & styling …          │
//   │   Food & beverage …          │
//   │   Production …               │
//   └──────────────────────────────┘
//
// The primary filters in the Must-have section render through
// FilterComponent with `showAsPopup={false}` + `liveEdit` — same
// inline pattern the existing SearchFiltersSecondary panel uses,
// so the keypath of FilterComponent + duck handlers stays
// untouched.
//
// The Nice-to-have section uses inline checkbox renderers that
// read/write `pref_*` URL params directly (no Sharetribe round-trip).
// Helpers come from the local addon-categories module so the URL
// encoding matches the existing AddOnFilters component.
//
// The old "Aesthetic" chip group was removed (2026-06-16): tags are
// now general keywords searched via the main keyword box (publicData
// .keywordsText text schema), not a curated client-side re-rank
// vocabulary. The `pref_aesthetic` re-rank tier in util/search.js is
// left intact but dormant — nothing sets the param now, and a stale
// bookmarked ?pref_aesthetic= URL still re-ranks harmlessly.

import React from 'react';
import classNames from 'classnames';

import { parse } from '../../../util/urlHelpers';
import {
  ADDON_TIERS,
  ADDON_TIER_LABELS,
  categoriesForTier,
} from '../../../data/addon-categories';

import FilterComponent from '../FilterComponent';
import LocationFilter from '../LocationFilter/LocationFilter';

import css from './SearchSidebar.module.css';

// ─── Nice-to-have URL param wiring ─────────────────────────────────

// pref_* param names the AddOnFilters component owns. Re-declared
// here to avoid a circular import; AddOnFilters exports the same
// mapping and there's a unit test asserting the two stay in sync.
const TIER_PREF_PARAM = {
  decor: 'pref_decor',
  fnb: 'pref_fnb',
  production: 'pref_production',
};

// Parse a comma-separated pref_* value into a Set, defensively
// filtering against the live catalog so a stale URL doesn't render
// chips selected for removed categories.
const addonSelectedSetFromParam = (paramValue, tier) => {
  if (typeof paramValue !== 'string' || paramValue.length === 0) {
    return new Set();
  }
  const validKeys = new Set(categoriesForTier(tier).map(c => c.key));
  return new Set(
    paramValue
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0 && validKeys.has(s))
  );
};

const paramFromAddonSelectedSet = selectedSet => {
  if (selectedSet.size === 0) return null;
  return Array.from(selectedSet).sort().join(',');
};

// ─── Reusable section header (badge + subtitle) ─────────────────────

const SectionHeader = ({ title, badge, badgeKind, subtitle }) => (
  <div className={css.sectionHeader}>
    <div className={css.sectionHeaderRow}>
      <h3 className={css.sectionTitle}>{title}</h3>
      <span
        className={classNames(css.sectionBadge, {
          [css.sectionBadgeRequired]: badgeKind === 'required',
          [css.sectionBadgePreference]: badgeKind === 'preference',
        })}
      >
        {badge}
      </span>
    </div>
    {subtitle && <p className={css.sectionSubtitle}>{subtitle}</p>}
  </div>
);

// ─── Add-on checkbox group (one per tier) ──────────────────────────

const AddOnCheckboxGroup = ({ tier, location, onFilterClick }) => {
  const paramKey = TIER_PREF_PARAM[tier];
  const queryParams = parse(location.search);
  const selectedSet = addonSelectedSetFromParam(queryParams[paramKey], tier);
  const categories = categoriesForTier(tier);

  const toggle = key => {
    if (!onFilterClick) return;
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onFilterClick(paramKey, paramFromAddonSelectedSet(next));
  };

  return (
    <div className={css.filterGroup}>
      <div className={css.filterGroupLabel}>{ADDON_TIER_LABELS[tier]}</div>
      {categories.map(cat => {
        const checked = selectedSet.has(cat.key);
        return (
          <label key={cat.key} className={css.checkboxRow}>
            <input
              type="checkbox"
              className={css.checkbox}
              checked={checked}
              onChange={() => toggle(cat.key)}
            />
            <span className={css.checkboxLabel}>{cat.label}</span>
          </label>
        );
      })}
    </div>
  );
};

// ─── Main sidebar ───────────────────────────────────────────────────

/**
 * @param {Object} props
 * @param {Array} props.primaryFilters — filter configs that should
 *   render in the Must-have section as inline FilterComponents.
 *   Source: SearchPageWithMap's `availablePrimaryFilters`.
 * @param {Object} props.commonFilterProps — shared props every
 *   FilterComponent needs (listingCategories, marketplaceCurrency,
 *   urlQueryParams, initialValues, getHandleChangedValueFn, intl).
 *   Passed through unchanged so this component stays orthogonal to
 *   the duck wiring.
 * @param {Object} props.location — router location, used for the
 *   nice-to-have URL-param reads.
 * @param {Function} props.onPreferenceChange — called with
 *   (paramName, value|null) when an add-on checkbox toggles. Same
 *   handler the page passes to AddOnFilters today.
 */
const SearchSidebar = ({
  primaryFilters,
  commonFilterProps,
  location,
  onPreferenceChange,
  onLocationChange,
}) => {
  return (
    <aside className={css.sidebar}>
      {/* ─── Must have ────────────────────────────────────────── */}
      <section className={css.section}>
        <SectionHeader
          title="Must have"
          badge="Required"
          badgeKind="required"
          subtitle="Hides kits that don't match"
        />
        <div className={css.sectionBody}>
          {/* Location is a must-have constraint (the kit has to reach the
              renter) — it sets the `bounds` param to scope results by area.
              Rendered first in the rail. */}
          <LocationFilter
            id="SearchSidebar.location"
            label="Location"
            location={location}
            onLocationChange={onLocationChange}
            containerId="SearchSidebar_MustHave"
          />
          {primaryFilters.map(filterConfig => {
            const key = `SearchSidebar.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
            const filterId = `SearchSidebar.${filterConfig.key.toLowerCase()}`;
            return (
              <FilterComponent
                key={key}
                id={filterId}
                config={filterConfig}
                containerId="SearchSidebar_MustHave"
                {...commonFilterProps}
                liveEdit
                showAsPopup={false}
              />
            );
          })}
        </div>
      </section>

      <div className={css.divider} />

      {/* ─── Nice to have ─────────────────────────────────────── */}
      <section className={css.section}>
        <SectionHeader
          title="Nice to have"
          badge="Preference"
          badgeKind="preference"
          subtitle="We'll highlight matches, but show you everything"
        />
        <div className={css.sectionBody}>
          {ADDON_TIERS.map(tier => (
            <AddOnCheckboxGroup
              key={tier}
              tier={tier}
              location={location}
              onFilterClick={onPreferenceChange}
            />
          ))}
        </div>
      </section>
    </aside>
  );
};

export default SearchSidebar;
